import { queryOptions, useQuery } from '@tanstack/react-query';

import { makeIsEnabled } from '@cast/utils';

import { apiClient } from '@cast/infrastructure-api';

import { overrideOrganizationHeader } from '@cast/api-client';
import { useOrganizations } from '@cast/shared-organization-context-data-access';

const PLATFORM_USAGE_DETAIL_QUERY_KEY = 'platform-usage-detail';

const getPlatformUsageDetailQueryKey = (params: {
  organizationId: string | undefined;
  feature?: string;
  periodFrom?: string;
  periodTo?: string;
}) =>
  [
    PLATFORM_USAGE_DETAIL_QUERY_KEY,
    params.organizationId,
    params.feature,
    params.periodFrom,
    params.periodTo,
  ] as const;

type GetPlatformUsageDetailQueryOptionsParams = {
  organizationId: string | undefined;
  feature?: string;
  periodFrom?: string;
  periodTo?: string;
};

const getPlatformUsageDetailQueryOptions = ({
  organizationId,
  feature,
  periodFrom,
  periodTo,
  enabled = true,
}: GetPlatformUsageDetailQueryOptionsParams & { enabled?: boolean }) =>
  queryOptions({
    queryKey: getPlatformUsageDetailQueryKey({
      organizationId,
      feature,
      periodFrom,
      periodTo,
    }),
    queryFn: async () => {
      const { data } = await apiClient.billing.getPlatformUsageDetail(
        {
          periodFrom: periodFrom!,
          periodTo: periodTo!,
          feature: feature!,
        },
        {
          headers: overrideOrganizationHeader(organizationId),
        }
      );
      return data;
    },
    enabled: makeIsEnabled(
      !!organizationId,
      !!feature,
      !!periodFrom,
      !!periodTo,
      enabled
    ),
  });

type UsePlatformUsageDetailQueryArgs = {
  feature?: string;
  periodFrom?: string;
  periodTo?: string;
  enabled?: boolean;
};

export const usePlatformUsageDetailQuery = ({
  feature,
  periodFrom,
  periodTo,
  enabled = true,
}: UsePlatformUsageDetailQueryArgs) => {
  const { currentOrganization } = useOrganizations();
  const { isLoading, isError, data, error, refetch } = useQuery(
    getPlatformUsageDetailQueryOptions({
      organizationId: currentOrganization?.id,
      feature,
      periodFrom,
      periodTo,
      enabled,
    })
  );

  return { isLoading, isError, data, error, refetch };
};
